<template>
  <table class="w-full">
    <caption>
      {{
        tableCaption
      }}
      ({{
        $t("Tooltips.ColumnHeadersWithButtonsAreSortable")
      }})
    </caption>
    <thead class="bg-gray-100">
      <tr class="border-b border-gray-300" role="row">
        <th v-if="isSectionSelectable" role="columnheader" scope="col"></th>
        <th
          class="h-14 pl-1 text-left"
          colspan="2"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn('name')"
        >
          <button
            id="name"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('name')"
            @click="sortSections"
          >
            {{ $t("name") }}
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>
        <th v-if="$slots.column2Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column2Header"></slot>
        </th>
        <th v-if="$slots.column3Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column3Header"></slot>
        </th>

        <th v-if="$slots.column4Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column4Header"></slot>
        </th>
      </tr>
    </thead>

    <tbody class="border-b border-gray-200">
      <tr
        v-for="section in sortedSections"
        :key="section.sectionId"
        role="row"
        class="border-b border-gray-200 hover:bg-gray-150"
        :class="{ 'cursor-pointer': isSectionSelectable || isSectionClickable }"
        :data-selected="isSectionSelected(section) && isSectionSelectable"
        @click.exact="select(section)"
      >
        <td v-if="isSectionSelectable">
          <div class="flex items-center h-full top-0 pr-2" js-table-checkbox-wrapper>
            <span
              class="flex items-center justify-center w-6 h-6 rounded-full bg-white border border-solid border-gray-200"
              js-table-checkbox
            >
              <span class="w-3 h-3 bg-primary-core rounded-full opacity-0"></span>
            </span>
          </div>
        </td>
        <td class="p-2 w-14">
          <SectionAvatar :section="section" :tooltip="false" :autoload-profile-photo="true" />
        </td>
        <td class="p-2 pl-2 w-auto">
          <a v-if="isSectionClickable" @click.stop.prevent="onClick(section)">{{ section.name }}</a>
          <span v-else> {{ section.name }} </span>
        </td>
        <td v-if="$slots.column2Content" class="p-2 pr-1 fit-content">
          <slot name="column2Content" :section="section"></slot>
        </td>
        <td v-if="$slots.column3Content" class="p-2 pr-1 fit-content">
          <slot name="column3Content" :section="section"></slot>
        </td>
        <td v-if="$slots.column4Content" class="p-2 pr-1 fit-content">
          <slot name="column4Content" :section="section"></slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import SectionAvatar from "@/components/Section/SectionAvatar.vue";
import { usePeopleStore } from "@/stores/people";
import { useSettingsStore } from "@/stores/settings";

defineOptions({ name: "SectionTable" });

interface Props {
  sections?: any[];
  disabledSectionIds?: any[];
  isSectionSelectable?: boolean;
  isSectionClickable?: boolean;
  tableCaption?: string;
}

const props = withDefaults(defineProps<Props>(), {
  sections: () => [],
  disabledSectionIds: () => [],
  isSectionSelectable: false,
  isSectionClickable: false,
  tableCaption: ""
});

const emit = defineEmits<{
  (e: "selectedSectionsChanged", sections: any[]): void;
  (e: "orderByDirectionChanged", direction: string): void;
  (e: "orderByPropertyChanged", property: string): void;
  (e: "sectionClick", section: any): void;
}>();

const peopleStore = usePeopleStore();
const settingsStore = useSettingsStore();

const orderByProperty = ref("name");
const orderByDirection = ref("asc");
const selectedSectionIds = ref<any[]>([]);

const sortedSections = computed(() => {
  const sortedList = props.sections;
  sortedList.sort((a, b) => a.name.localeCompare(b.name, settingsStore.language));
  if (orderByDirection.value === "desc") {
    sortedList.reverse();
  }
  return sortedList;
});

const onClick = section => {
  emit("sectionClick", section);
};

const selectedSections = () => {
  const list: any[] = [];
  selectedSectionIds.value.map(si => {
    list.push(props.sections.find(s => s.sectionId === si));
  });
  return list;
};

const select = section => {
  if (!props.isSectionSelectable) {
    onClick(section);
  }

  if (props.disabledSectionIds && props.disabledSectionIds.indexOf("section.sectionId") > -1) return;

  const index = selectedSectionIds.value.indexOf(section.sectionId);
  if (index > -1) {
    selectedSectionIds.value.splice(index, 1);
  } else {
    selectedSectionIds.value.push(section.sectionId);
  }

  emit("selectedSectionsChanged", selectedSections());
};

const sortSections = evnt => {
  const elmId = evnt.target.id || evnt.target.parentElement.id;
  if (orderByProperty.value === elmId) {
    orderByDirection.value = orderByDirection.value === "asc" ? "desc" : "asc";
    emit("orderByDirectionChanged", orderByDirection.value);
  } else {
    orderByProperty.value = elmId;
    emit("orderByPropertyChanged", orderByProperty.value);
  }
};

const loadProfilePicturesForList = sectionList => {
  peopleStore.loadProfilePicturesFromList(sectionList);
};

const isSectionSelected = section => selectedSectionIds.value.indexOf(section.sectionId) > -1;

const calculateSortDirectionForColumn = columnName => {
  if (orderByProperty.value === columnName) {
    return orderByDirection.value === "asc" ? "ascending" : "descending";
  } else {
    return undefined;
  }
};

onMounted(() => {
  loadProfilePicturesForList(props.sections);
});

watch(
  () => props.sections,
  list => {
    loadProfilePicturesForList(list);
  }
);
</script>

<style scoped>
.min-width {
  min-width: 1.2rem;
}

td.fit-content {
  width: 1px;
  white-space: nowrap;
}
</style>
